import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });

  // Get the intended destination from location state
  const from = location.state?.from?.pathname || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(formData.email, formData.password, formData.rememberMe);
      toast.success('Logged in successfully!');
      
      // If there's a saved destination, go there
      if (from) {
        navigate(from, { replace: true });
        return;
      }
      
      // Otherwise, redirect based on role
      if (user.role === 'ADMIN') {
        const hasFacilities = user.assignedFacilities?.length > 0;
        navigate(hasFacilities ? '/dashboard' : '/facility-management', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-[#F8F9FA] px-4 py-8"
      data-testid="login-page"
    >
      <Card className="w-full max-w-md shadow-md">
        <CardHeader className="space-y-4 pb-2">
          {/* Centered Logo */}
          <div className="flex justify-center">
            <img 
              src="/ors-logo.svg" 
              alt="Onsite Rail Solutions" 
              className="h-12 w-auto"
              srcSet="/ors-logo.svg 1x, /ors-logo@2x.png 2x"
              data-testid="login-logo"
            />
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#323033]">Welcome back</h1>
            <p className="text-sm text-[#5A5A5A] mt-1">Sign in to your account to continue</p>
          </div>
        </CardHeader>

        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#808080] hover:text-[#323033]"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rememberMe"
                  checked={formData.rememberMe}
                  onCheckedChange={(checked) => setFormData({ ...formData, rememberMe: checked })}
                  data-testid="remember-me-checkbox"
                />
                <label htmlFor="rememberMe" className="text-sm text-[#5A5A5A] cursor-pointer">
                  Keep me logged in
                </label>
              </div>
              <Link
                to="/forgot-password"
                className="text-sm text-[#005EF8] hover:text-[#0047C2] font-medium"
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#005EF8] hover:bg-[#0047C2] text-white h-11"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-[#5A5A5A]">
            Don't have an account?{' '}
            <Link
              to="/signup"
              className="text-[#005EF8] hover:text-[#0047C2] font-medium"
              data-testid="signup-link"
            >
              Sign up for free
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
